import { useContext, useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { cacheImage, loadCachedImage } from './imageCache'; // Import helper functions
import { Auth_MerchantID_context } from '../../../storeState/auth_merchantID_context';

async function resizeAndCompressImage(imageBlob, maxWidth = 500, maxHeight = 500, quality = 0.7) {
    return new Promise((resolve) => {
        const img = document.createElement('img');
        const reader = new FileReader();

        reader.onload = (event) => {
            img.src = event.target.result;
        };

        img.onload = () => {
            const canvas = document.createElement('canvas');
            let width = img.width;
            let height = img.height;

            // Scale image down if needed
            if (width > maxWidth) {
                height *= maxWidth / width;
                width = maxWidth;
            }
            if (height > maxHeight) {
                width *= maxHeight / height;
                height = maxHeight;
            }

            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);

            // Compress to JPEG format
            const compressedBase64 = canvas.toDataURL('image/jpeg', quality);
            //alert("Compressed Image Size:"+(compressedBase64.length * 3) / (4 * 1024 * 1024));
            resolve(compressedBase64);
        };

        reader.readAsDataURL(imageBlob);
    });
}

// Compress & Store Image in localStorage
async function cacheImageWeb(url, key, baseURL) {
    try {
        const response = await fetch(baseURL + url);
        const blob = await response.blob();
        const fileSizeInMB = blob.size / (1024 * 1024);
        //alert("Original Image Size: "+fileSizeInMB.toFixed(2));

        if (fileSizeInMB > 5) { // Skip large images
            //alert("Image is too large for localStorage, skipping caching.");
            return null;
        }

        // Compress Image Before Storing
        const compressedBase64 = await resizeAndCompressImage(blob);

        localStorage.setItem(key, compressedBase64);
        //alert("Cached Compressed Image:"+key);
        return compressedBase64;
    } catch (error) {
        console.error("Error caching image in web:", error);
        return null;
    }
}

// Retrieve Cached Image from localStorage
function getCachedImageWeb(key) {
    return localStorage.getItem(key);
}

// Compress & Store Image in Capacitor (Native)
async function cacheImageNative(url, fileName, baseURL) {
    try {
        const response = await fetch(baseURL + url);
        const blob = await response.blob();
        const compressedBase64 = await resizeAndCompressImage(blob);

        await cacheImage(compressedBase64, fileName);
        //alert("Cached Compressed Image (Native):"+fileName);
        return compressedBase64;
    } catch (error) {
        console.error(" Error caching image in Native:", error);
        return null;
    }
}

export function CachedImage({ imageUrl, fileName, className, alt, onError}) {
    const authenticatedMerchantContext = useContext(Auth_MerchantID_context);
    const [baseURL] = useState(authenticatedMerchantContext.baseURL);
    const [imageSrc, setImageSrc] = useState(null);

    useEffect(() => {
        async function fetchImage() {
            if (Capacitor.isNativePlatform()) {
                try {
                    const cachedImg = await loadCachedImage(fileName);
                    if (cachedImg) {
                        setImageSrc(cachedImg);
                    } else {
                        //alert("Uploading and caching in Native");
                        const newCachedImage = await cacheImageNative(imageUrl, fileName, baseURL);
                        setImageSrc(newCachedImage || imageUrl);
                    }
                } catch (error) {
                    console.error("Error fetching cached image (Native):", error);
                    setImageSrc(imageUrl);
                }
            } else {
                const cachedImg = getCachedImageWeb(fileName);
                if (cachedImg) {
                    setImageSrc(cachedImg);
                } else {
                    //alert("Uploading and caching in Web");
                    const newCachedImage = await cacheImageWeb(imageUrl, fileName, baseURL);
                    setImageSrc(newCachedImage || imageUrl);
                }
            }
        }

        fetchImage();
    }, [imageUrl, fileName]);

    return <img src={imageSrc || imageUrl} className={className} alt={alt || "Cached Image"} onError={onError}/>;
}
